import React from "react";
import { useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AlertCircle, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import JobDescriptionViewer, { JobDetails, Header } from "./JobDescriptionViewer";

/**
 * PublicJobLayout provides the consistent sidebar, header, and branding 
 * for all ATS forms.
 */
const PublicJobLayout = ({
    children,
    jobDetails,
    organizationName,
    branding,
    isWhiteLabelingEnable,
    isLoading,
    applicationClosed,
    applicationSubmitted,
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const view = searchParams.get("view") || "description"; // "description" | "apply"

    const setView = (newView) => {
        setSearchParams((prev) => {
            prev.set("view", newView);
            return prev;
        });
    };

    const formatJobDescription = (jobRole, data, location, questionType) => {
        if (!data) return null;

        if (questionType === "screening" || questionType === "Initial Screening" || questionType === "Bring Your Own Questions") {
            return (
                <div className="space-y-5">
                    <div>
                        <h4 className={`text-lg font-medium ${isWhiteLabelingEnable ? 'text-slate-600' : 'text-[#C1A0D0]'} mb-3`}>
                            {jobRole}
                        </h4>
                        <div className={`space-y-2 text-sm ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-gray-300'}`}>
                            <div className="flex items-center space-x-2 text-[15px]">
                                <span>
                                    Experience:{" "}
                                    <span className={`${isWhiteLabelingEnable ? 'text-gray-600 ' : 'text-white'} font-medium`}>
                                        {data.minExp || 0} - {data.maxExp || 1} years
                                    </span>
                                </span>
                            </div>
                            {location && (
                                <div className={`flex items-center space-x-2 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-white"}`}>
                                    <span>
                                        Location:{" "}
                                        <span className={`${isWhiteLabelingEnable ? "text-gray-600" : 'text-white'}  font-medium`}>
                                            {location}
                                        </span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {(data.job_description || data.Raw_Job_Description) && (
                        <div>
                            <h5 className={`text-md font-medium ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-white'} mb-3 border-b border-gray-400 pb-2`}>
                                Job Description
                            </h5>
                            <p className={`${isWhiteLabelingEnable ? 'text-gray-600 ' : 'text-gray-300'} text-[15px] leading-relaxed whitespace-pre-wrap`}>
                                {data.Raw_Job_Description ?? data.job_description}
                            </p>
                        </div>
                    )}
                </div>
            );
        }

        const experience = ((data.minExp || 0) + (data.maxExp || 1)) / 2;
        const responsibilities = data.Roles_and_Responsibilities?.slice(0, 5) || [];

        return (
            <div className="space-y-5">
                <div>
                    <h4 className={`text-lg font-medium ${isWhiteLabelingEnable ? 'text-slate-600' : 'text-[#C1A0D0]'} mb-3`}>{jobRole}</h4>
                    <div className={`space-y-2 text-sm ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-gray-300'}`}>
                        <div className="flex items-center space-x-2 text-[15px]">
                            <span>
                                Experience:{" "}
                                <span className={`${isWhiteLabelingEnable ? 'text-gray-600 ' : 'text-white'} font-medium`}>
                                    {data.minExp || 0} - {data.maxExp || 1} years
                                </span>
                            </span>
                        </div>
                        {location && (
                            <div className={`flex items-center space-x-2 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-white"}`}>
                                <span>
                                    Location:{" "}
                                    <span className={`${isWhiteLabelingEnable ? "text-gray-600" : 'text-white'}  font-medium`}>{location}</span>
                                </span>
                            </div>
                        )}
                    </div>
                </div>
                {responsibilities.length > 0 && (
                    <div>
                        <h5 className={`text-md font-medium ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-white'} mb-3 border-b border-gray-400 pb-2`}>
                            Key Responsibilities
                        </h5>
                        <div className="space-y-2">
                            {responsibilities.map((item, i) => (
                                <div key={i} className="flex items-start space-x-2">
                                    <span className={`${isWhiteLabelingEnable ? 'text-gray-500' : 'text-[#C1A0D0]'} mt-1 text-xs flex-shrink-0`}>
                                        ●
                                    </span>
                                    <p className={`${isWhiteLabelingEnable ? 'text-gray-600 ' : ' text-gray-300'} text-[16px] leading-relaxed`}>
                                        {item}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {data.Required_Skills && (
                    <div>
                        <h5 className={`text-md font-medium ${isWhiteLabelingEnable ? 'text-gray-700' : 'text-white'} mb-3 border-b border-gray-400 pb-2`}>
                            Required Skills
                        </h5>
                        {data.Required_Skills?.skill_based ? (
                            <div className="space-y-4">
                                {data.Required_Skills.skill_based.slice(0, 6).map((skill, i) => (
                                    <div key={i} className="space-y-2">
                                        <div className="flex items-start space-x-2">
                                            <span className={`${isWhiteLabelingEnable ? 'text-gray-500' : 'text-[#C1A0D0]'} mt-1 text-xs flex-shrink-0`}>
                                                ●
                                            </span>
                                            <div className="flex-1">
                                                <span className={`${isWhiteLabelingEnable ? 'text-gray-600' : 'text-gray-300'} font-medium text-sm`}>
                                                    {skill.skill_name}
                                                </span>
                                                {skill.topics?.length > 0 && (
                                                    <span className={`${isWhiteLabelingEnable ? 'text-gray-600' : 'text-gray-300'} text-sm ml-2`}>
                                                        ({skill.topics.join(", ")})
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {(experience < 4 ? data.Required_Skills.Medium : data.Required_Skills.Hard)?.slice(0, 6).map((skill, i) => (
                                    <span key={i} className={` px-3 py-1.5 rounded-lg text-[15px]  transition-colors ${isWhiteLabelingEnable ? 'bg-slate-400/10 border border-[#C1A0D0]/20 text-gray-600 hover:bg-[#6ac5fe]/20' : 'bg-[#C1A0D0]/10 border border-[#C1A0D0]/20 text-gray-300 hover:bg-[#C1A0D0]/20'}`}>
                                        {/* {skill} */}
                                        {Array.isArray(skill) ? skill.join(", ") : skill}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen  flex items-center justify-center ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'}`}>
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            </div>
        );
    }

    if (applicationClosed) {
        return (
            <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center p-4`}>
                <div className="max-w-md w-full">
                    <div className="glass-effect bg-white rounded-3xl p-8 text-center">
                        <div className="mb-6">
                            <AlertCircle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                        </div>
                        <h2 className="text-2xl font-medium text-gray-700 mb-4">Position Closed</h2>
                        <p className="text-gray-600 mb-6">This position is no longer accepting new applications.</p>
                        <p className="text-gray-600 text-sm">Thank you for your interest! Please explore other opportunities on the company's career page.</p>
                    </div>
                </div>
                <ToastContainer position="top-right" />
            </div>
        );
    }

    if (!jobDetails) {
        return (
            <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center`}>
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h2 className={`text-2xl font-medium mb-2 ${isWhiteLabelingEnable ? 'text-gray-700 ' : 'text-gray-300'}`}>Invalid Link</h2>
                    <p className="text-gray-500">This job application link is invalid or has expired.</p>
                </div>
            </div>
        );
    }

    if (applicationSubmitted) {
        return (
            <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#FAF9F6]' : 'app-background'} flex items-center justify-center p-4`}>
                <div className="max-w-2xl w-full ">
                    <div className={`rounded-3xl p-6 text-center ${isWhiteLabelingEnable ? 'bg-white shadow-sm backdrop-blur-sm ' : 'glass-effect'}`}>
                        <div className="flex justify-end items-center">
                            <XCircle className={`${isWhiteLabelingEnable ? 'text-gray-700' : 'text-white'}`} onClick={() => window.history.back()} />
                        </div>
                        <div className="mb-6">
                            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                        </div>
                        <h2 className={`text-3xl font-medium mb-4 ${isWhiteLabelingEnable ? 'text-gray-700 ' : 'text-white'}`}>Thank You!</h2>
                        <p className={` mb-1 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>
                            Your application for <span className={`font-medium  ${isWhiteLabelingEnable ? 'text-[#1569c4] ' : 'text-[#C1A0D0]'}`}>{jobDetails.job_role}</span> has been submitted to <span className={`font-medium  ${isWhiteLabelingEnable ? 'text-[#1569c4] ' : 'text-[#C1A0D0]'}`}>{branding?.companyName}</span>
                        </p>
                        <p className={` mb-4 ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>Our recruiter will reach out to you soon.</p>
                        <div className="flex justify-center items-center mt-3 space-x-2">
                            <span className={` text-sm ${isWhiteLabelingEnable ? 'text-gray-600' : "text-gray-300"}`}>Powered by GenieHire</span>
                        </div>
                    </div>
                </div>
                <ToastContainer position="top-right" />
            </div>
        );
    }

    if (view === "description") {
        return (
            <div className="min-h-screen bg-[#F5F4FA]">
                <JobDescriptionViewer
                    jobDetails={jobDetails}
                    organizationName={organizationName}
                    branding={branding}
                    isWhiteLabelingEnable={isWhiteLabelingEnable}
                    isLoading={isLoading}
                    onApply={() => setView("apply")}
                />
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isWhiteLabelingEnable ? 'bg-[#F1F4FB]' : 'app-background'}`}>
            <style>
                {`
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        textarea:-webkit-autofill,
        textarea:-webkit-autofill:hover,
        textarea:-webkit-autofill:focus {
          -webkit-text-fill-color: ${isWhiteLabelingEnable ? "#4A4A4A" : "#D1D5DB"} !important;
          box-shadow: 0 0 0px 1000px ${isWhiteLabelingEnable ? "white" : "transperent"} inset !important;
          transition: background-color 9999s ease-in-out 0s !important;
        },
        .PhoneInputInput {
          color: rgba(209, 213, 219, 1) !important;
        }
      `}
            </style>

            {/* Header matching JobDescriptionViewer */}
            <Header 
                jobDetails={jobDetails}
                organizationName={organizationName}
                branding={branding}
                isWhiteLabelingEnable={isWhiteLabelingEnable}
            />

            <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
                <div className="flex flex-col lg:flex-row gap-8 items-start">

                    {/* LEFT – ATS Form */}
                    <div className="flex-1 flex flex-col gap-2">

                        {/* Back Button */}
                        <div className="-mt-7">
                            <button
                                onClick={() => setView("description")}
                                className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                                    isWhiteLabelingEnable 
                                        ? 'text-gray-500 hover:text-gray-900' 
                                        : 'text-gray-300 hover:text-white'
                                }`}
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Back to Job Description
                            </button>
                        </div>

                        {/* Form Container */}
                        <div>
                            {children}
                        </div>
                    </div>

                    {/* RIGHT – Job Details sticky sidebar */}
                    <div className="w-full lg:w-80 lg:sticky lg:top-8">
                        <div className={`${isWhiteLabelingEnable ? 'bg-[#FAFAFA]' : 'glass-effect'} rounded-2xl p-6 shadow`}>
                            <JobDetails jobDetails={jobDetails} isWhiteLabelingEnable={isWhiteLabelingEnable} />
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer position="top-right" />
        </div>
    );
};

export default PublicJobLayout;
